import React, { useEffect, useMemo, useState } from "react";
import { getAllowedPrubehKeysForScenario } from "../data/orderTemplates";
import { buildCalendarEventsFromOrder, buildCalendarEvent } from "../utils/calendarEventFactory";
import { detectCalendarConflicts, getEffectiveRange, rangesOverlap } from "../utils/calendarConflicts";
import {
  getEventTypeLabel,
  getPersonRoleLabel,
  getVehicleRoleLabel,
  STAFF_OPTIONS,
  SPEAKER_OPTIONS,
  MUSIC_TYPE_OPTIONS,
  MUSIC_PERSON_OPTIONS,
  VEHICLE_OPTIONS,
} from "../utils/calendarRoles";
import { getCenikItem } from "../../data/cenikUtils";
import {
  CEREMONY_PLACE_GROUPS,
  CEREMONY_VARIANTS,
  CHURCH_SERVICE_TYPES,
  computeCeremonyTiming,
  getCeremonyPlaceLabel,
} from "../utils/ceremonyPlaces";

const panelStyle = {
  marginTop: 18,
  marginBottom: 18,
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  padding: 18,
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
};

const topRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const weekGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 12,
};

const dayCardStyle = (active) => ({
  border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
  borderRadius: 16,
  padding: 12,
  background: active ? "#eff6ff" : "#fff",
  boxShadow: active ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
  cursor: "pointer",
});

const miniItemStyle = {
  marginTop: 8,
  padding: 10,
  borderRadius: 12,
  background: "#f9fafb",
  border: "1px solid #f1f5f9",
};

const actionBtnStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  fontWeight: 700,
  cursor: "pointer",
};

const pickerInputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#111827",
  fontSize: 14,
  boxSizing: "border-box",
};

const timePickerWrapStyle = {
  marginTop: 10,
  padding: 10,
  borderRadius: 12,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
};

const timeGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 8,
  marginTop: 8,
};

const timeBtnStyle = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 13,
};

const typeGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 8,
  marginTop: 10,
};

const typeBtnStyle = {
  padding: "9px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 13,
};

const placeGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 8,
  marginTop: 10,
};

const placeBtnStyle = {
  padding: "9px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#111827",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 13,
};

const infoBoxStyle = {
  marginTop: 8,
  fontSize: 12,
  color: "#475569",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "6px 8px",
  lineHeight: 1.4,
};

const warnBoxStyle = {
  marginTop: 8,
  fontSize: 12,
  color: "#92400e",
  background: "#fffbeb",
  border: "1px solid #fde68a",
  borderRadius: 10,
  padding: "6px 8px",
  lineHeight: 1.4,
};

const errorBoxStyle = {
  marginTop: 8,
  fontSize: 12,
  color: "#991b1b",
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  borderRadius: 10,
  padding: "6px 8px",
  lineHeight: 1.4,
};

const successBoxStyle = {
  marginTop: 8,
  fontSize: 12,
  color: "#166534",
  background: "#f0fdf4",
  border: "1px solid #bbf7d0",
  borderRadius: 10,
  padding: "6px 8px",
  lineHeight: 1.4,
};

const TIME_OPTIONS = [
  "08:00", "09:00", "10:00",
  "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00",
];

const TYPE_OPTIONS = [
  { value: "kremace", label: "Kremace" },
  { value: "kremace_bez_obradu", label: "Kremace bez obřadu" },
  { value: "pohreb", label: "Pohřeb" },
  { value: "obrad_s_urnou", label: "Obřad s urnou" },
  { value: "alternativa", label: "Alternativa" },
  { value: "prevoz", label: "Převoz" },
  { value: "ostatni", label: "Ostatní" },
];

const PLACE_TYPE_OPTIONS = [
  { value: "obradni_sin", label: "Obřadní síň" },
  { value: "kostel", label: "Kostel" },
  { value: "jine", label: "Jiné místo" },
];

const SIMPLE_KREMACE_BEZ_OBRADU_PRUBEHY = [
  "bez_obradu",
  "bez_obradu_urna",
  "bez_obradu_vystaveni",
];

function pad(n) {
  return String(n).padStart(2, "0");
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfWeekMonday(dateLike = new Date()) {
  const d = new Date(dateLike);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(dateLike, n) {
  const d = new Date(dateLike);
  d.setDate(d.getDate() + n);
  return d;
}

function formatDateLabel(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("cs-CZ", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildDisabledOptionLabel(label, disabled) {
  return disabled ? `${label} • obsazeno` : label;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
}

function formatRangeLabel(start, end) {
  const s = formatTime(start);
  const e = formatTime(end);
  if (!s && !e) return "";
  if (s && e) return `${s}–${e}`;
  return s || e || "";
}

function getBusyLabel(baseLabel, busyFrom, busyTo) {
  const range = formatRangeLabel(busyFrom, busyTo);
  if (!range) return `${baseLabel} • obsazeno`;
  return `${baseLabel} • obsazen ${range}`;
}

function getPickerPlaceOptions(placeType) {
  const key = String(placeType || "").trim();
  const group = CEREMONY_PLACE_GROUPS.find((x) => x.key === key);
  return group?.items || [];
}

function getDraftObradTiming(placeId = "", ceremonyVariant = "", churchServiceType = "") {
  return computeCeremonyTiming({
    placeId,
    ceremonyVariant,
    churchServiceType,
  });
}

function buildDraftPickerEvent({
  day = "",
  time = "",
  type = "",
  place = "",
  ceremonyVariant = "",
  churchServiceType = "",
  leader = "",
  support = "",
  speaker = "",
  speakerCustom = "",
  vehicle = "",
}) {
  const d = String(day || "").slice(0, 10);
  const t = String(time || "").slice(0, 5);
  const typ = String(type || "").trim();
  const misto = String(place || "").trim();
  const mistoLabel = getCeremonyPlaceLabel(misto) || misto;

    const speakerAssignment = normalizeSpeakerPerson(speaker, speakerCustom, leader);
if (!d || !t || !typ) return null;

  const start = new Date(`${d}T${t}:00`);
  if (Number.isNaN(start.getTime())) return null;

  if (typ === "prevoz") {
    const end = new Date(start.getTime() + 60 * 60000);
    return buildCalendarEvent({
      id: `draft_prevoz_${d}_${t}`,
      orderId: "__draft__",
      typUdalosti: "prevoz",
      stav: "navrh",
      isTentative: true,
      start,
      end,
      mistoNazev: mistoLabel,
      mistoAdresa: "",
      mistoTyp: "prevoz",
      roleAssignments: [
        leader ? { role: "ridic_prevozu", personId: leader, personLabel: leader } : null,
        support && support !== leader ? { role: "doprovod_prevozu", personId: support, personLabel: support } : null,
      ].filter(Boolean),
      vehicleAssignments: vehicle
        ? [{ role: "prevozove_auto", vehicleId: vehicle, vehicleLabel: vehicle }]
        : [],
    });
  }

  const timing = getDraftObradTiming(misto, ceremonyVariant, churchServiceType);
  const end = new Date(start.getTime() + (timing?.durationMin || 60) * 60000);

  return buildCalendarEvent({
    id: `draft_obrad_${d}_${t}`,
    orderId: "__draft__",
    typUdalosti: "obrad",
    stav: "navrh",
    isTentative: true,
    start,
    end,
    bufferBeforeMin: timing?.bufferBeforeMin || 0,
    bufferAfterMin: timing?.bufferAfterMin || 0,
    mistoNazev: mistoLabel,
    mistoAdresa: "",
    mistoTyp: "obrad",
    roleAssignments: [
        leader ? { role: "vedouci_obradu", personId: leader, personLabel: leader } : null,
        support && support !== leader ? { role: "obradni_sluzba", personId: support, personLabel: support } : null,
        speakerAssignment,
      ].filter(Boolean),
    vehicleAssignments: vehicle
      ? [{ role: "hlavni_vuz", vehicleId: vehicle, vehicleLabel: vehicle }]
      : [],
  });
}

function getEventPeopleSet(event) {
  return new Set(
    (Array.isArray(event?.roleAssignments) ? event.roleAssignments : [])
      .map((x) => String(x?.personId || "").trim())
      .filter(Boolean)
  );
}

function getEventVehicleSet(event) {
  return new Set(
    (Array.isArray(event?.vehicleAssignments) ? event.vehicleAssignments : [])
      .map((x) => String(x?.vehicleId || "").trim())
      .filter(Boolean)
  );
}

function buildOrderSource(item) {
  return item?.formData || item?.data || item?.order || item?.item || item || {};
}

function getOrderId(src, item) {
  return String(
    item?.id ||
      item?.orderId ||
      item?.zakazkaId ||
      src?.id ||
      src?.orderId ||
      src?.zakazkaId ||
      src?.meta?.id ||
      src?.meta?.orderId ||
      ""
  ).trim();
}

function getConflictKey(conflict) {
  if (!conflict) return "";
  if (conflict.type === "missing_assignments") return `${conflict.type}:${conflict.eventId}`;
  return `${conflict.type}:${conflict.eventAId}:${conflict.eventBId}`;
}

function buildConflictIndex(conflicts) {
  const map = new Map();
  for (const conflict of conflicts || []) {
    if (conflict?.eventId) {
      if (!map.has(conflict.eventId)) map.set(conflict.eventId, []);
      map.get(conflict.eventId).push(conflict);
    }
    if (conflict?.eventAId) {
      if (!map.has(conflict.eventAId)) map.set(conflict.eventAId, []);
      map.get(conflict.eventAId).push(conflict);
    }
    if (conflict?.eventBId) {
      if (!map.has(conflict.eventBId)) map.set(conflict.eventBId, []);
      map.get(conflict.eventBId).push(conflict);
    }
  }
  return map;
}

function renderSharedAssignments(conflict) {
  if (!Array.isArray(conflict?.sharedAssignments) || !conflict.sharedAssignments.length) return "";
  return conflict.sharedAssignments
    .map((x) => {
      if (conflict.type === "person") {
        return `${x.personLabel || x.personId} (${getPersonRoleLabel(x.roleA)} / ${getPersonRoleLabel(x.roleB)})`;
      }
      if (conflict.type === "vehicle") {
        return `${x.vehicleLabel || x.vehicleId} (${getVehicleRoleLabel(x.roleA)} / ${getVehicleRoleLabel(x.roleB)})`;
      }
      return "";
    })
    .filter(Boolean)
    .join(" • ");
}

function summarizeConflict(conflict) {
  if (!conflict) return "";
  if (conflict.type === "person") {
    return `Kolize člověka: ${renderSharedAssignments(conflict) || conflict.reason}`;
  }
  if (conflict.type === "vehicle") {
    return `Kolize auta: ${renderSharedAssignments(conflict) || conflict.reason}`;
  }
  if (conflict.type === "place") {
    return `Kolize místa: ${conflict.placeLabel || conflict.reason}`;
  }
  if (conflict.type === "missing_assignments") {
    const missingPeople = (conflict.missingPeople || []).map((x) => x.label).join(", ");
    const missingVehicles = (conflict.missingVehicles || []).map((x) => x.label).join(", ");
    const parts = [];
    if (missingPeople) parts.push(`chybí lidi: ${missingPeople}`);
    if (missingVehicles) parts.push(`chybí auta: ${missingVehicles}`);
    return `Neobsazeno: ${parts.join(" • ") || conflict.reason}`;
  }
  return conflict.reason || "";
}

function badgeStyle(type) {
  const base = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };

  if (type === "obrad") return { ...base, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" };
  if (type === "prevoz") return { ...base, background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" };
  if (type === "sjednani") return { ...base, background: "#f5f3ff", color: "#6d28d9", borderColor: "#ddd6fe" };
  if (type === "kolize") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  if (type === "soft") return { ...base, background: "#fff7ed", color: "#9a3412", borderColor: "#fed7aa" };
  if (type === "ok") return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#d1d5db" };
  return { ...base, background: "#f9fafb", color: "#111827", borderColor: "#e5e7eb" };
}

function normalizeCalendarInvoiceDateOnly(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function computeCalendarVyjezdInvoiceStatus(src = {}) {
  const prevoz = src?.prevoz || {};
  const vyjezd = src?.vyjezd || {};
  const zaklad = src?.zaklad || {};

  const aktivniPcrVetev =
    typeof prevoz?.fakturaceRozpad?.pcr?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pcr.aktivni
      : String(zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "").trim().toLowerCase() === "pcr";

  const aktivniPojistovnaVetev =
    typeof prevoz?.fakturaceRozpad?.pojistovna?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pojistovna.aktivni
      : String(zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "").trim().toLowerCase() === "pojistovna";

  const aktivniRodinnaVetev =
    typeof prevoz?.fakturaceRozpad?.rodina?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.rodina.aktivni
      : !!prevoz?.navazujiciRodinnyPrevoz;

  const faktura =
    aktivniPcrVetev
      ? (prevoz?.externiFakturace?.pcr || vyjezd?.externiFakturace?.pcr || {})
      : aktivniPojistovnaVetev
        ? (prevoz?.externiFakturace?.pojistovna || vyjezd?.externiFakturace?.pojistovna || {})
        : aktivniRodinnaVetev
          ? (prevoz?.rodinnaFakturace || {})
          : {};

  const rawStav = String(faktura?.stav || "").trim().toLowerCase();
  const datumSplatnosti = normalizeCalendarInvoiceDateOnly(faktura?.datumSplatnosti || faktura?.splatnost);
  const odeslanoDne = String(faktura?.odeslanoDne || "").trim();
  const today = new Date().toISOString().slice(0, 10);

  if (["uhrazeno", "zaplaceno", "proplaceno", "uhrazena", "uhrazená", "uhrazeno_cele"].includes(rawStav)) {
    return { id: "uhrazeno", label: "Uhrazeno", tone: "green" };
  }

  if (datumSplatnosti && datumSplatnosti < today) {
    return { id: "po_splatnosti", label: "Po splatnosti", tone: "red" };
  }

  if (odeslanoDne || ["odeslano", "vystaveno", "cekame_na_uhradu", "čekáme_na_úhradu"].includes(rawStav)) {
    return { id: "odeslano", label: "Odesláno", tone: "blue" };
  }

  return { id: "neodeslano", label: "Neodesláno", tone: "slate" };
}

function calendarVyjezdInvoiceBadgeStyle(status) {
  const tone = String(status?.tone || "slate");
  if (tone === "green") return { display: "inline-block", padding: "4px 8px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: "#dcfce7", color: "#166534", border: "1px solid #86efac", whiteSpace: "nowrap" };
  if (tone === "red") return { display: "inline-block", padding: "4px 8px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", whiteSpace: "nowrap" };
  if (tone === "blue") return { display: "inline-block", padding: "4px 8px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd", whiteSpace: "nowrap" };
  return { display: "inline-block", padding: "4px 8px", borderRadius: 999, fontSize: 12, fontWeight: 800, background: "#f3f4f6", color: "#4b5563", border: "1px solid #d1d5db", whiteSpace: "nowrap" };
}

function computeCalendarFamilyStatus(src = {}) {
  const navazuje = !!src?.prevoz?.navazujiciRodinnyPrevoz;
  if (navazuje) {
    return { id: "navazuje_rodina", label: "Navazuje rodinná část", tone: "green" };
  }
  return { id: "jen_vyjezd", label: "Jen výjezd", tone: "slate" };
}

function calendarFamilyBadgeStyle(status) {
  const tone = String(status?.tone || "slate");
  if (tone === "green") {
    return {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
      whiteSpace: "nowrap",
    };
  }
  return {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: "#f8fafc",
    color: "#475569",
    border: "1px solid #cbd5e1",
    whiteSpace: "nowrap",
  };
}

function computeCalendarOrderStatus(src = {}) {
  const zaklad = src?.zaklad || {};
  const obrad = src?.obrad || {};
  const prevoz = src?.prevoz || {};
  const platba = src?.platba || {};

  const rawState = String(
    zaklad?.stavZakazky ||
    src?.stavKalendar ||
    src?.stavZakazky ||
    ""
  ).trim().toLowerCase();

  const obradDatum = String(obrad?.datum || "").trim();
  const obradCas = String(obrad?.cas || "").trim();
  const prevozDatum = String(prevoz?.datumPrevozu || "").trim();
  const prevozCas = String(prevoz?.casPrevozu || "").trim();

  let eventDateTime = null;

  if (obradDatum) {
    const dt = `${obradDatum}T${(obradCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  } else if (prevozDatum) {
    const dt = `${prevozDatum}T${(prevozCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  }

  const now = new Date();
  const eventPassed = !!eventDateTime && eventDateTime.getTime() <= now.getTime();

  const uhrazeno = Number(platba?.uhrazenoCelkem || 0) || 0;
  const zaloha = Number(platba?.zalohovaPlatba || 0) || 0;

  if (rawState.includes("storno")) {
    return { id: "storno", label: "Storno", tone: "red" };
  }

  if (rawState.includes("hotovo") || rawState.includes("uzavreno") || rawState.includes("uzavřeno")) {
    return { id: "hotovo", label: "Hotovo", tone: "green" };
  }

  if (eventPassed) {
    if (uhrazeno > 0) {
      return { id: "hotovo", label: "Hotovo", tone: "green" };
    }
    return { id: "cek_fakturace", label: "Hotovo / čeká na fakturaci", tone: "amber" };
  }

  if (zaloha > 0 && uhrazeno <= 0) {
    return { id: "doplatek", label: "Čeká na doplatek", tone: "amber" };
  }

  if (eventDateTime) {
    return { id: "cek_obrad", label: "Čeká na obřad", tone: "blue" };
  }

  return { id: "rozpracovano", label: "Rozpracováno", tone: "slate" };
}

function computeCalendarFinanceStatus(src = {}) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const platba = src?.platba || {};
  const sluzby = Array.isArray(src?.sluzby) ? src.sluzby : [];
  const kvetiny = src?.kvetiny || {};

  let servicesTotal = 0;
  for (const item of sluzby) {
    if (!item) continue;

    const cenik = getCenikItem(item?.id);
    if (!cenik) continue;

    const rakevItem = item?.rakevId ? getCenikItem(item.rakevId) : null;
    const cenaAktualni =
      item?.id === "rakev" && rakevItem
        ? Number(rakevItem?.cenaBezDph) || 0
        : item?.cenaBezDphOverride !== "" && item?.cenaBezDphOverride !== undefined
          ? Number(item?.cenaBezDphOverride) || 0
          : Number(cenik?.cenaBezDph) || 0;

    const qty = Number(item?.mnozstvi) || 0;
    const bez = round2(cenaAktualni * qty);
    const dph = round2((bez * (Number(cenik?.dph) || 0)) / 100);
    const celkem = round2(bez + dph);

    servicesTotal += celkem;
  }

  const normalizeKvetina = (item) => ({
    objednano: !!item?.objednano,
    dodavatel: item?.dodavatel || "",
    typVazby: item?.typVazby || "",
    barva: item?.barva || "",
    stuhaBarva: item?.stuhaBarva || "",
    stuhaText1: item?.stuhaText1 || "",
    stuhaText2: item?.stuhaText2 || "",
    poznamka: item?.poznamka || "",
    mnozstvi: Number(item?.mnozstvi || 1) || 1,
    cenaBezDph: Number(item?.cenaBezDph || 0) || 0,
    cenaSDph: Number(item?.cenaSDph || 0) || 0,
    dph: Number(item?.dph || 0) || 0,
  });

  const isFilledKvetina = (item) =>
    !!(
      item?.objednano ||
      String(item?.dodavatel || "").trim() ||
      String(item?.typVazby || "").trim() ||
      String(item?.barva || "").trim() ||
      String(item?.stuhaBarva || "").trim() ||
      String(item?.stuhaText1 || "").trim() ||
      String(item?.stuhaText2 || "").trim() ||
      String(item?.poznamka || "").trim() ||
      (Number(item?.cenaSDph || 0) || 0) > 0 ||
      (Number(item?.cenaBezDph || 0) || 0) > 0
    );

  const rawKvetinyItems = Array.isArray(kvetiny?.items) ? kvetiny.items : [];
  const kvetinyItems = rawKvetinyItems.length > 0
    ? rawKvetinyItems.map(normalizeKvetina).filter(isFilledKvetina)
    : (() => {
        const legacy = normalizeKvetina(kvetiny || {});
        return isFilledKvetina(legacy) ? [legacy] : [];
      })();

  const flowersTotal = kvetinyItems.reduce((acc, item) => {
    const mnozstvi = Number(item?.mnozstvi || 1) || 1;
    const bez = round2((Number(item?.cenaBezDph || 0) || 0) * mnozstvi);
    const dphCastka = round2((bez * (Number(item?.dph || 0) || 0)) / 100);
    const celkem = round2(bez + dphCastka);
    return acc + celkem;
  }, 0);

  const rawZaloha =
    src?.finance?.zalohaCastka ??
    platba?.zalohovaPlatba ??
    platba?.zalohaCastka ??
    src?.zalohaCastka ??
    0;

  const zaloha = round2(Number(rawZaloha) || 0);

  const rawUhrazeno =
    src?.finance?.uhrazenoCelkem ??
    platba?.uhrazenoCelkem ??
    src?.uhrazenoCelkem;

  const uhrazeno = round2(
    rawUhrazeno === "" || rawUhrazeno === null || rawUhrazeno === undefined
      ? zaloha
      : (Number(rawUhrazeno) || 0)
  );

  const total = round2(servicesTotal + flowersTotal);
  const outstanding = round2(Math.max(0, total - uhrazeno));

  if (total <= 0 && uhrazeno <= 0) {
    return { id: "bez_castek", label: "Bez částek", tone: "slate", total };
  }

  if (outstanding <= 0 && total > 0) {
    return { id: "uhrazeno", label: "Uhrazeno", tone: "green", total };
  }

  if (uhrazeno > 0) {
    return { id: "castecne", label: "Částečně uhrazeno", tone: "amber", total };
  }

  return { id: "neuhrazeno", label: "Neuhrazeno", tone: "red", total };
}

function calendarFinanceBadgeStyle(status) {
  const tone = String(status?.tone || "slate");
  const base = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };

  if (tone === "green") return { ...base, background: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" };
  if (tone === "amber") return { ...base, background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" };
  if (tone === "red") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  return { ...base, background: "#f8fafc", color: "#475569", borderColor: "#cbd5e1" };
}

function calendarStatusBadgeStyle(status) {
  const tone = String(status?.tone || "slate");

  if (tone === "green") {
    return {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }

  if (tone === "blue") {
    return {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    };
  }

  if (tone === "amber") {
    return {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
      background: "#fffbeb",
      color: "#92400e",
      border: "1px solid #fde68a",
    };
  }

  if (tone === "red") {
    return {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
      background: "#fef2f2",
      color: "#991b1b",
      border: "1px solid #fecaca",
    };
  }

  return {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
    background: "#f8fafc",
    color: "#475569",
    border: "1px solid #cbd5e1",
  };
}

function rowCardStyle(row) {
  const base = {
    ...miniItemStyle,
    borderLeft: "5px solid #cbd5e1",
    boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
  };

  if (row?.hardConflicts?.length) {
    return {
      ...base,
      background: "#fff1f2",
      border: "1px solid #fecdd3",
      borderLeft: "5px solid #e11d48",
      boxShadow: "0 0 0 2px rgba(225,29,72,0.10)",
    };
  }

  if (row?.softConflicts?.length) {
    return {
      ...base,
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      borderLeft: "5px solid #ea580c",
    };
  }

  const statusTone = String(row?.orderStatus?.tone || "");

  if (statusTone === "red") {
    return {
      ...base,
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderLeft: "5px solid #dc2626",
    };
  }

  if (statusTone === "green") {
    return {
      ...base,
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      borderLeft: "5px solid #16a34a",
    };
  }

  if (statusTone === "amber") {
    return {
      ...base,
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderLeft: "5px solid #d97706",
    };
  }

  if (statusTone === "blue") {
    return {
      ...base,
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderLeft: "5px solid #2563eb",
    };
  }

  if (row?.kind === "obrad") {
    return {
      ...base,
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderLeft: "5px solid #2563eb",
    };
  }

  if (row?.kind === "prevoz") {
    return {
      ...base,
      background: "#ecfdf5",
      border: "1px solid #a7f3d0",
      borderLeft: "5px solid #059669",
    };
  }

  if (row?.kind === "sjednani") {
    return {
      ...base,
      background: "#f5f3ff",
      border: "1px solid #ddd6fe",
      borderLeft: "5px solid #7c3aed",
    };
  }

  return base;
}

function mapTypeToKind(type) {
  if (type === "prevoz") return "prevoz";
  if (type === "obrad") return "obrad";
  if (type === "sjednani") return "sjednani";
  return "ok";
}

function getTemplateTypZakazky(template) {
  const src = template?.data || template?.order || template?.formData || {};
  return String(
    src?.zaklad?.typZakazky ||
      template?.typZakazky ||
      template?.type ||
      ""
  ).trim();
}

function getTemplateCoolingScenario(template) {
  const src = template?.data || template?.order || template?.formData || {};
  return String(
    src?.prevoz?.coolingScenario ||
      template?.coolingScenario ||
      ""
  ).trim();
}

function getTemplateDeathScenario(template) {
  const src = template?.data || template?.order || template?.formData || {};
  return String(
    src?.zaklad?.scenarUmrti ||
      template?.scenarUmrti ||
      ""
  ).trim();
}

function getTemplatePrubehKey(template) {
  const src = template?.data || template?.order || template?.formData || {};
  return String(
    src?.zaklad?.prubehKey ||
      template?.prubehKey ||
      ""
  ).trim();
}


function normalizeSpeakerPerson(speakerId = "", speakerCustom = "", leaderId = "") {
  const sid = String(speakerId || "").trim();
  const scustom = String(speakerCustom || "").trim();
  const lleader = String(leaderId || "").trim();

  if (!sid) return null;
  if (sid === "bez_recnice") return null;
  if (sid === "zadna") return null;

  if (sid === lleader && sid !== "dalsi") {
    return null;
  }

  if (sid === "dalsi") {
    if (!scustom) return null;
    return {
      role: "recnice",
      personId: `speaker_custom:${String(scustom).trim().toLowerCase()}`,
      personLabel: scustom,
    };
  }

  const found = (Array.isArray(SPEAKER_OPTIONS) ? SPEAKER_OPTIONS : []).find(
    (x) => String(x?.id || "").trim() === sid
  );

  return {
    role: "recnice",
    personId: sid,
    personLabel: String(found?.label || sid).trim(),
  };
}

function getSpeakerFromSource(src = {}) {
  const obrad = src?.obrad || {};
  const rec = src?.rec || {};

  const speakerId = String(
    obrad?.speaker ||
    obrad?.speakerId ||
    obrad?.recnik ||
    obrad?.recnikId ||
    rec?.speaker ||
    rec?.speakerId ||
    ""
  ).trim();

  const speakerCustom = String(
    obrad?.speakerCustom ||
    obrad?.recnikCustom ||
    rec?.speakerCustom ||
    ""
  ).trim();

  const leaderId = String(
    obrad?.leader ||
    obrad?.vedouci ||
    obrad?.vedouciObradu ||
    ""
  ).trim();

  return normalizeSpeakerPerson(speakerId, speakerCustom, leaderId);
}

function getPrubehOptionsForTyp(typZakazky, selectedPlaceLabel = "", selectedPlaceType = "") {
  const t = String(typZakazky || "").trim();
  const placeLabel = String(selectedPlaceLabel || "").trim();
  const placeType = String(selectedPlaceType || "").trim();

  const ceremonyLabel =
    placeLabel ||
    (placeType === "kostel" ? "Kostel" : "Obřadní síň");

  if (t === "kremace") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obradni_sin_ck_nasledna_kremace", label: `${ceremonyLabel} / následná kremace` },
      { value: "kostel_nasledna_kremace", label: "Kostel / následná kremace" },
      { value: "cb_krematorium_rozlouceni", label: "Rozloučení v krematoriu" },
    ];
  }

  if (t === "kremace_bez_obradu") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "bez_obradu", label: "Bez obřadu" },
      { value: "bez_obradu_urna", label: "Bez obřadu + následné rozloučení s urnou" },
      { value: "bez_obradu_vystaveni", label: "Kremace bez obřadu + vystavení zesnulého" },
    ];
  }

  if (t === "pohreb") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obradni_sin_ck_hrob", label: `${ceremonyLabel} / pohřbení do hrobu` },
      { value: "kostel_hrob", label: "Kostel / pohřbení do hrobu" },
      { value: "bez_obradu_hrob", label: "Bez obřadu / pohřbení do hrobu" },
      { value: "prime_pohrbeni_hrob", label: "Přímé pohřbení do hrobu" },
    ];
  }

  if (t === "alternativa") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "alternativa_standard", label: "Alternativní rozloučení" },
    ];
  }

  if (t === "obrad_s_urnou") {
    return [
      { value: "", label: "Vyber další průběh" },
      { value: "obrad_s_urnou_standard", label: "Obřad s urnou" },
    ];
  }

  return [{ value: "", label: "Bez výběru" }];
}

function extractCalendarEntries(item) {
  const src = buildOrderSource(item);
  const orderId = getOrderId(src, item);
  const events = buildCalendarEventsFromOrder(src);

  const zaklad = src?.zaklad || {};
  const vypravitel = src?.vypravitel || {};
  const vstupni = src?.vstupniInformace || {};
  const zesnula = src?.zesnulaOsoba || {};

  const zesnuly =
    String(
      zaklad?.zesnuly ||
        [vstupni?.jmenoZesnuleho, vstupni?.prijmeniZesnuleho].filter(Boolean).join(" ") ||
        [zesnula?.jmeno, zesnula?.prijmeni].filter(Boolean).join(" ") ||
        item?.zesnuly ||
        item?.nazev ||
        ""
    ).trim() || "Bez jména";

  const objednavatel = String(
    [vypravitel?.jmeno || "", vypravitel?.prijmeni || ""].filter(Boolean).join(" ")
  ).trim();

  const out = events.map((ev) => {
    const start = ev?.start ? new Date(ev.start) : null;
    const date = start && !Number.isNaN(start.getTime()) ? toIsoDate(start) : "";
    const sourceSpeaker = getSpeakerFromSource(src);
      const mergedRoles = [
        ...(Array.isArray(ev.roleAssignments) ? ev.roleAssignments : []),
        sourceSpeaker,
      ].filter(Boolean);

      const uniqueRoles = [];
      const seenRoles = new Set();
      for (const role of mergedRoles) {
        const key = `${String(role?.role || "").trim()}__${String(role?.personId || "").trim()}`;
        if (!key.trim() || seenRoles.has(key)) continue;
        seenRoles.add(key);
        uniqueRoles.push(role);
      }

      return {
      id: ev.id,
      orderId: ev.orderId || orderId,
      kind: mapTypeToKind(ev.typUdalosti),
      typUdalosti: ev.typUdalosti,
      title: getEventTypeLabel(ev.typUdalosti),
      date,
      time: formatTime(ev.start),
      place: ev.mistoNazev || "",
      placeAddress: ev.mistoAdresa || "",
      zesnuly: ev.zesnuly || zesnuly,
      objednavatel: ev.objednavatel || objednavatel,
      note: ev.poznamka || "",
      farar: ev.farar || "",
        orderStatus: computeCalendarOrderStatus(src),
          familyStatus: computeCalendarFamilyStatus(src),
          vyjezdInvoiceStatus: computeCalendarVyjezdInvoiceStatus(src),
        financeStatus: computeCalendarFinanceStatus(src),
        start: ev.start,
        end: ev.end,
      roleAssignments: uniqueRoles,
      vehicleAssignments: ev.vehicleAssignments || [],
      raw: item,
    };
  });

  if (!out.length && String(zaklad?.datumSjednani || "").trim()) {
    out.push({
      id: `${orderId}-sjednani`,
      orderId,
      kind: "sjednani",
      typUdalosti: "sjednani",
      title: "Sjednání",
      date: String(zaklad.datumSjednani || "").slice(0, 10),
      time: "",
      place: "",
      placeAddress: "",
      zesnuly,
      objednavatel,
      note: String(zaklad?.poznamka || "").trim(),
        orderStatus: computeCalendarOrderStatus(src),
          familyStatus: computeCalendarFamilyStatus(src),
          vyjezdInvoiceStatus: computeCalendarVyjezdInvoiceStatus(src),
        financeStatus: computeCalendarFinanceStatus(src),
        start: "",
        end: "",
      roleAssignments: [],
      vehicleAssignments: [],
      raw: item,
    });
  }

  return out.filter((x) => x.date);
}

export default function Pohrebka2CalendarPanel({
  ordersList = [],
  allTemplates = [],
  selectedDate = "",
  onSelectDate,
  onCreateFromDate,
  onOpenOrder,
}) {
  const [pickerDay, setPickerDay] = useState("");
  const [pickerTime, setPickerTime] = useState("");
  const [pickerType, setPickerType] = useState("");
  const [pickerPlaceType, setPickerPlaceType] = useState("");
  const [pickerPlace, setPickerPlace] = useState("");
  const [pickerCeremonyVariant, setPickerCeremonyVariant] = useState("");
  const [pickerChurchServiceType, setPickerChurchServiceType] = useState("");
  const [pickerClient, setPickerClient] = useState("");
  const [pickerDeceased, setPickerDeceased] = useState("");
  const [pickerNote, setPickerNote] = useState("");
  const [pickerLeader, setPickerLeader] = useState("");
  const [pickerSupport, setPickerSupport] = useState("");
  const [pickerSpeaker, setPickerSpeaker] = useState("");
  const [pickerSpeakerCustom, setPickerSpeakerCustom] = useState("");
  const [pickerMusicType, setPickerMusicType] = useState("");
  const [pickerMusicPerson, setPickerMusicPerson] = useState("");
  const [pickerMusicPersonCustom, setPickerMusicPersonCustom] = useState("");
  const [pickerVehicle, setPickerVehicle] = useState("");
  const [pickerFarar, setPickerFarar] = useState("");
  const [pickerTemplate, setPickerTemplate] = useState("");
  const [pickerDeathScenario, setPickerDeathScenario] = useState("");
  const [pickerPrubeh, setPickerPrubeh] = useState("");
  const [pickerPlaceAddressKrematorium, setPickerPlaceAddressKrematorium] = useState("");

  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [familyFilter, setFamilyFilter] = useState("");
  const [vyjezdInvoiceFilter, setVyjezdInvoiceFilter] = useState("");

  const isKremaceBezObraduSimple =
    String(pickerType || "").trim() === "kremace_bez_obradu" &&
    SIMPLE_KREMACE_BEZ_OBRADU_PRUBEHY.includes(String(pickerPrubeh || "").trim());

  const isKremaceBezObraduVystaveni =
    isKremaceBezObraduSimple &&
    String(pickerPrubeh || "").trim() === "bez_obradu_vystaveni";

  const needsCeremonyPlace =
    !isKremaceBezObraduSimple || isKremaceBezObraduVystaveni;

  const showFararField = !isKremaceBezObraduSimple;
  const showSpeakerField = !isKremaceBezObraduSimple;
  const showMusicField = !isKremaceBezObraduSimple;
  const showSupportField = true;

  const requiresClassicCeremonyPlace =
    !isKremaceBezObraduSimple && String(pickerType || "").trim() !== "prevoz";

  const goToPreviousWeek = () => setWeekStart((prev) => addDays(prev, -7));
  const goToCurrentWeek = () => setWeekStart(startOfWeekMonday(new Date()));
  const goToNextWeek = () => setWeekStart((prev) => addDays(prev, 7));

  useEffect(() => {
    if (!isKremaceBezObraduSimple) return;
    if (isKremaceBezObraduVystaveni) {
      if (pickerPlaceType !== "obradni_sin" && pickerPlaceType !== "jine") {
        setPickerPlaceType("obradni_sin");
      }
      return;
    }
    if (!pickerPlaceType) setPickerPlaceType("jine");
  }, [isKremaceBezObraduSimple, isKremaceBezObraduVystaveni, pickerPlaceType]);

  useEffect(() => {
    if (!isKremaceBezObraduVystaveni) return;
    if (pickerPlaceType === "obradni_sin" && !pickerPlace) {
      setPickerPlace("obradni_sin_ck");
    }
  }, [isKremaceBezObraduVystaveni, pickerPlaceType, pickerPlace]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = addDays(weekStart, i);
      return toIsoDate(d);
    });
  }, [weekStart]);

  const weekRangeLabel = useMemo(() => {
    const start = weekStart;
    const end = addDays(weekStart, 6);
    const format = (d) =>
      d.toLocaleDateString("cs-CZ", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    return `${format(start)} – ${format(end)}`;
  }, [weekStart]);

  const entries = useMemo(() => {
    const flat = (Array.isArray(ordersList) ? ordersList : []).flatMap(extractCalendarEntries);

    const eventModels = flat.map((x) => {
      const eventModel = buildCalendarEvent({
        id: x.id,
        orderId: x.orderId,
        typUdalosti: x.typUdalosti,
        start: x.start,
        end: x.end,
        mistoNazev: x.place,
        mistoAdresa: x.placeAddress,
        roleAssignments: x.roleAssignments,
        vehicleAssignments: x.vehicleAssignments,
      });
      return { ...x, eventModel };
    });

    const conflictResult = detectCalendarConflicts(
      eventModels.map((x) => ({
        id: x.id,
        orderId: x.orderId,
        typUdalosti: x.typUdalosti,
        start: x.start,
        end: x.end,
        mistoNazev: x.place,
        mistoAdresa: x.placeAddress,
        roleAssignments: x.roleAssignments,
        vehicleAssignments: x.vehicleAssignments,
      }))
    );

    const conflictIndex = buildConflictIndex(conflictResult.all || []);

    let out = eventModels
      .map((x) => {
        const conflicts = conflictIndex.get(x.id) || [];
        const unique = [];
        const seen = new Set();

        for (const c of conflicts) {
          const key = getConflictKey(c);
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(c);
          }
        }

        return {
          ...x,
          conflicts: unique,
          hardConflicts: unique.filter((c) => c.severity === "hard"),
          softConflicts: unique.filter((c) => c.severity !== "hard"),
        };
      })
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return String(a.time || "").localeCompare(String(b.time || ""));
      });

  if (familyFilter) {
    out = out.filter((row) => String(row.familyStatus?.id || "") === familyFilter);
  }

  return out;
  }, [ordersList, familyFilter, vyjezdInvoiceFilter]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const day of weekDays) map.set(day, []);
    for (const item of entries) {
      if (map.has(item.date)) map.get(item.date).push(item);
    }
    return map;
  }, [entries, weekDays]);

  const availablePlacesForPicker = useMemo(() => {
    const placeOptions = getPickerPlaceOptions(pickerPlaceType).map((item) => ({
      ...item,
      value: item.id,
    }));

    if (!pickerDay || !pickerTime || !pickerType || !pickerPlaceType) {
      return placeOptions;
    }

    return placeOptions.map((item) => {
      const probe = buildDraftPickerEvent({
        day: pickerDay,
        time: pickerTime,
        type: pickerType,
        place: item.id,
        ceremonyVariant: pickerCeremonyVariant,
        churchServiceType: pickerChurchServiceType,
        leader: pickerLeader,
          support: pickerSupport,
          speaker: pickerSpeaker,
          speakerCustom: pickerSpeakerCustom,
          vehicle: pickerVehicle,
      });

      if (!probe) {
        return { ...item, disabled: false, reason: "" };
      }

      const probeRange = getEffectiveRange(probe);
      const probePlace = String(probe?.mistoNazev || "").trim();

      let disabled = false;
      let reason = "";

      for (const row of entries) {
        const ev = row?.eventModel;
        if (!ev) continue;

        const evPlace = String(ev?.mistoNazev || "").trim();
        if (!evPlace || !probePlace) continue;
        if (evPlace !== probePlace) continue;

        const evRange = getEffectiveRange(ev);
        if (
          rangesOverlap(
            probeRange.effectiveStart,
            probeRange.effectiveEnd,
            evRange.effectiveStart,
            evRange.effectiveEnd
          )
        ) {
          disabled = true;
          reason = `obsazeno ${formatRangeLabel(evRange.effectiveStart, evRange.effectiveEnd)}`;
          break;
        }
      }

      return {
        ...item,
        disabled,
        reason,
      };
    });
  }, [
    pickerDay,
    pickerTime,
    pickerType,
    pickerPlaceType,
    pickerCeremonyVariant,
    pickerChurchServiceType,
    pickerLeader,
    pickerSupport,
    pickerVehicle,
    entries,
  ]);

  const availableLeaderOptions = useMemo(() => {
    const selectedDay = String(pickerDay || "").trim();
    const selectedTime = String(pickerTime || "").trim();
    const pickedSupport = String(pickerSupport || "").trim();

    return STAFF_OPTIONS.map((item) => {
      const id = String(item.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (id !== "dalsi") {
        if (pickedSupport && id === pickedSupport) {
          disabled = true;
        }

        if (selectedDay && selectedTime) {
          const probe = buildDraftPickerEvent({
            day: selectedDay,
            time: selectedTime,
            type: pickerType || "obrad",
            place: pickerPlace || "",
            ceremonyVariant: pickerCeremonyVariant,
            churchServiceType: pickerChurchServiceType,
            leader: id,
              support: pickedSupport,
              speaker: pickerSpeaker,
              speakerCustom: pickerSpeakerCustom,
              vehicle: pickerVehicle || "",
          });

          if (probe) {
            const probeRange = getEffectiveRange(probe);

            for (const row of entries) {
              const ev = row?.eventModel;
              if (!ev) continue;

              const evPeople = Array.isArray(ev?.roleAssignments) ? ev.roleAssignments : [];
              const hasSamePerson = evPeople.some((x) => String(x?.personId || "").trim() === id);
              if (!hasSamePerson) continue;

              const evRange = getEffectiveRange(ev);
              if (
                rangesOverlap(
                  probeRange.effectiveStart,
                  probeRange.effectiveEnd,
                  evRange.effectiveStart,
                  evRange.effectiveEnd
                )
              ) {
                disabled = true;
                busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
                busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
                break;
              }
            }
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [pickerDay, pickerTime, pickerType, pickerPlace, pickerCeremonyVariant, pickerChurchServiceType, pickerSupport, pickerVehicle, entries]);

  
  const availableSpeakerOptions = useMemo(() => {
    const selectedDay = String(pickerDay || "").trim();
    const selectedTime = String(pickerTime || "").trim();
    const pickedLeader = String(pickerLeader || "").trim();

    return SPEAKER_OPTIONS.map((item) => {
      const id = String(item.id || "").trim();

      if (!id || id === "bez_recnice") {
        return { ...item, disabled: false, displayLabel: item.label };
      }

      if (pickedLeader && id === pickedLeader) {
        return { ...item, disabled: false, displayLabel: item.label };
      }

      if (id === "dalsi") {
        return { ...item, disabled: false, displayLabel: item.label };
      }

      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (selectedDay && selectedTime) {
        const probe = buildDraftPickerEvent({
          day: selectedDay,
          time: selectedTime,
          type: pickerType || "obrad",
          place: pickerPlace || "",
          ceremonyVariant: pickerCeremonyVariant,
          churchServiceType: pickerChurchServiceType,
          leader: pickedLeader,
          support: pickerSupport || "",
          speaker: id,
          speakerCustom: "",
          vehicle: pickerVehicle || "",
        });

        if (probe) {
          const probeRange = getEffectiveRange(probe);

          for (const row of entries) {
            const ev = row?.eventModel;
            if (!ev) continue;

            const evPeople = Array.isArray(ev?.roleAssignments) ? ev.roleAssignments : [];
            const hasSamePerson = evPeople.some((x) => String(x?.personId || "").trim() === id);
            if (!hasSamePerson) continue;

            const evRange = getEffectiveRange(ev);
            if (
              rangesOverlap(
                probeRange.effectiveStart,
                probeRange.effectiveEnd,
                evRange.effectiveStart,
                evRange.effectiveEnd
              )
            ) {
              disabled = true;
              busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
              busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
              break;
            }
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [
    pickerDay,
    pickerTime,
    pickerType,
    pickerPlace,
    pickerCeremonyVariant,
    pickerChurchServiceType,
    pickerLeader,
    pickerSupport,
    pickerVehicle,
    entries,
  ]);

const availableSupportOptions = useMemo(() => {
    const selectedDay = String(pickerDay || "").trim();
    const selectedTime = String(pickerTime || "").trim();
    const pickedLeader = String(pickerLeader || "").trim();

    return STAFF_OPTIONS.map((item) => {
      const id = String(item.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (id !== "dalsi") {
        if (pickedLeader && id === pickedLeader) {
          disabled = true;
        }

        if (selectedDay && selectedTime) {
          const probe = buildDraftPickerEvent({
            day: selectedDay,
            time: selectedTime,
            type: pickerType || "obrad",
            place: pickerPlace || "",
            ceremonyVariant: pickerCeremonyVariant,
            churchServiceType: pickerChurchServiceType,
            leader: pickedLeader,
              support: id,
              speaker: pickerSpeaker,
              speakerCustom: pickerSpeakerCustom,
              vehicle: pickerVehicle || "",
          });

          if (probe) {
            const probeRange = getEffectiveRange(probe);

            for (const row of entries) {
              const ev = row?.eventModel;
              if (!ev) continue;

              const evPeople = Array.isArray(ev?.roleAssignments) ? ev.roleAssignments : [];
              const hasSamePerson = evPeople.some((x) => String(x?.personId || "").trim() === id);
              if (!hasSamePerson) continue;

              const evRange = getEffectiveRange(ev);
              if (
                rangesOverlap(
                  probeRange.effectiveStart,
                  probeRange.effectiveEnd,
                  evRange.effectiveStart,
                  evRange.effectiveEnd
                )
              ) {
                disabled = true;
                busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
                busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
                break;
              }
            }
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [pickerDay, pickerTime, pickerType, pickerPlace, pickerCeremonyVariant, pickerChurchServiceType, pickerLeader, pickerVehicle, entries]);

  const availableVehicleOptions = useMemo(() => {
    const selectedDay = String(pickerDay || "").trim();
    const selectedTime = String(pickerTime || "").trim();

    return VEHICLE_OPTIONS.map((item) => {
      const id = String(item.id || "").trim();
      let disabled = false;
      let busyFrom = "";
      let busyTo = "";

      if (selectedDay && selectedTime) {
        const probe = buildDraftPickerEvent({
          day: selectedDay,
          time: selectedTime,
          type: pickerType || "obrad",
          place: pickerPlace || "",
          ceremonyVariant: pickerCeremonyVariant,
          churchServiceType: pickerChurchServiceType,
          leader: pickerLeader || "",
            support: pickerSupport || "",
            speaker: pickerSpeaker,
            speakerCustom: pickerSpeakerCustom,
            vehicle: id,
        });

        if (probe) {
          const probeRange = getEffectiveRange(probe);

          for (const row of entries) {
            const ev = row?.eventModel;
            if (!ev) continue;

            const evVehicles = Array.isArray(ev?.vehicleAssignments) ? ev.vehicleAssignments : [];
            const hasSameVehicle = evVehicles.some((x) => String(x?.vehicleId || "").trim() === id);
            if (!hasSameVehicle) continue;

            const evRange = getEffectiveRange(ev);
            if (
              rangesOverlap(
                probeRange.effectiveStart,
                probeRange.effectiveEnd,
                evRange.effectiveStart,
                evRange.effectiveEnd
              )
            ) {
              disabled = true;
              busyFrom = evRange.effectiveStart ? evRange.effectiveStart.toISOString() : "";
              busyTo = evRange.effectiveEnd ? evRange.effectiveEnd.toISOString() : "";
              break;
            }
          }
        }
      }

      return {
        ...item,
        disabled,
        busyFrom,
        busyTo,
        displayLabel: disabled ? getBusyLabel(item.label, busyFrom, busyTo) : item.label,
      };
    });
  }, [pickerDay, pickerTime, pickerType, pickerPlace, pickerCeremonyVariant, pickerChurchServiceType, pickerLeader, pickerSupport, entries]);

  const timeOptionStatuses = useMemo(() => {
    if (!pickerDay || !pickerType || !pickerPlace) {
      return TIME_OPTIONS.map((time) => ({ time, disabled: false, reason: "" }));
    }

    return TIME_OPTIONS.map((time) => {
      const draftEvent = buildDraftPickerEvent({
        day: pickerDay,
        time,
        type: pickerType,
        place: pickerPlace,
        ceremonyVariant: pickerCeremonyVariant,
        churchServiceType: pickerChurchServiceType,
        leader: pickerLeader,
          support: pickerSupport,
          speaker: pickerSpeaker,
          speakerCustom: pickerSpeakerCustom,
          vehicle: pickerVehicle,
      });

      if (!draftEvent) return { time, disabled: false, reason: "" };

      const draftRange = getEffectiveRange(draftEvent);
      const draftPeople = getEventPeopleSet(draftEvent);
      const draftVehicles = getEventVehicleSet(draftEvent);
      const draftPlace = String(draftEvent?.mistoNazev || "").trim();

      let disabled = false;
      let reason = "";

      for (const item of entries) {
        const ev = item?.eventModel;
        if (!ev) continue;

        const evRange = getEffectiveRange(ev);
        if (
          !rangesOverlap(
            draftRange.effectiveStart,
            draftRange.effectiveEnd,
            evRange.effectiveStart,
            evRange.effectiveEnd
          )
        ) {
          continue;
        }

        const evPlace = String(ev?.mistoNazev || "").trim();
        const samePlace = draftPlace && evPlace && draftPlace === evPlace;
        if (samePlace) {
          disabled = true;
          reason = `obsazeno místem ${formatRangeLabel(evRange.effectiveStart, evRange.effectiveEnd)}: ${item.zesnuly || item.title || item.orderId || "-"}`;
          break;
        }

        const samePeople = [...draftPeople].some((id) => getEventPeopleSet(ev).has(id));
        if (samePeople) {
          disabled = true;
          reason = `obsazeno člověkem ${formatRangeLabel(evRange.effectiveStart, evRange.effectiveEnd)}: ${item.zesnuly || item.title || item.orderId || "-"}`;
          break;
        }

        const sameVehicles = [...draftVehicles].some((id) => getEventVehicleSet(ev).has(id));
        if (sameVehicles) {
          disabled = true;
          reason = `obsazeno autem ${formatRangeLabel(evRange.effectiveStart, evRange.effectiveEnd)}: ${item.zesnuly || item.title || item.orderId || "-"}`;
          break;
        }
      }

      return { time, disabled, reason };
    });
  }, [pickerDay, pickerType, pickerPlace, pickerCeremonyVariant, pickerChurchServiceType, pickerLeader, pickerSupport, pickerVehicle, entries]);

  const selectedTimeStatus = timeOptionStatuses.find((x) => x.time === pickerTime) || null;

  const availableTemplateOptions = useMemo(() => {
    const currentType = String(pickerType || "").trim();
    const currentDeathScenario = String(pickerDeathScenario || "").trim();
    const currentPrubeh = String(pickerPrubeh || "").trim();
    const allowedPrubehKeys = getAllowedPrubehKeysForScenario(currentType, currentDeathScenario);
    if (!currentType) return [];

    const items = Array.isArray(allTemplates) ? allTemplates : [];

    return items
      .map((tpl) => {
        const id = String(tpl?.id || "").trim();
        const label = String(tpl?.name || tpl?.label || id).trim();
        const typ = getTemplateTypZakazky(tpl);
        const coolingScenario = getTemplateCoolingScenario(tpl);
        const deathScenario = getTemplateDeathScenario(tpl);
        const prubehKey = getTemplatePrubehKey(tpl);
        const fullLabel = coolingScenario === "wagnerka" ? `${label} • Wagnerka` : label;

        return { id, label: fullLabel, typ, raw: tpl, coolingScenario, deathScenario, prubehKey };
      })
      .filter((tpl) => {
        if (!(tpl.id && tpl.label && tpl.typ === currentType)) return false;
        if (currentDeathScenario && String(tpl.deathScenario || "").trim() !== currentDeathScenario) return false;
        if (allowedPrubehKeys.length && !allowedPrubehKeys.includes(String(tpl.prubehKey || "").trim())) return false;
        if (currentPrubeh && String(tpl.prubehKey || "").trim() !== currentPrubeh) return false;
        return true;
      })
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), "cs"));
  }, [pickerType, pickerDeathScenario, pickerPrubeh, allTemplates]);

  useEffect(() => {
    if (!pickerType) {
      if (pickerPrubeh) setPickerPrubeh("");
      return;
    }

    const allowed = getPrubehOptionsForTyp(pickerType).filter((item) =>
      getAllowedPrubehKeysForScenario(pickerType, pickerDeathScenario).includes(String(item?.value || "").trim())
    );

    const currentValue = String(pickerPrubeh || "").trim();

    if (currentValue && allowed.some((item) => String(item?.value || "").trim() === currentValue)) {
      return;
    }

    if (allowed.length === 1) {
      setPickerPrubeh(String(allowed[0]?.value || ""));
      return;
    }

    if (String(pickerType || "").trim() === "kremace_bez_obradu" && !currentValue && allowed.length > 0) {
      setPickerPrubeh(String(allowed[0]?.value || ""));
      return;
    }

    if (currentValue) setPickerPrubeh("");
  }, [pickerType, pickerDeathScenario, pickerPrubeh]);

  const selectedPlaceMeta = useMemo(() => {
    return getPickerPlaceOptions(pickerPlaceType).find((item) => item.id === pickerPlace) || null;
  }, [pickerPlaceType, pickerPlace]);

  const selectedTiming = useMemo(() => {
    if (!pickerPlace || pickerType === "prevoz") return null;
    return getDraftObradTiming(pickerPlace, pickerCeremonyVariant, pickerChurchServiceType);
  }, [pickerPlace, pickerType, pickerCeremonyVariant, pickerChurchServiceType]);

  const isCeskyKrumlovCeremonyHall = useMemo(() => {
    return String(selectedPlaceMeta?.id || "").trim() === "sin_ck";
  }, [selectedPlaceMeta]);

  const showCeremonyVariantPicker = useMemo(() => {
    if (!pickerType || pickerType === "prevoz") return false;
    if (isKremaceBezObraduSimple) return false;
    if (!pickerPlaceType || !pickerPlace) return false;
    return pickerPlaceType === "obradni_sin" || pickerPlaceType === "kostel";
  }, [pickerType, pickerPlaceType, pickerPlace, isKremaceBezObraduSimple]);

  const showChurchServiceTypePicker = useMemo(() => {
    if (!pickerType || pickerType === "prevoz") return false;
    if (isKremaceBezObraduSimple) return false;
    return pickerPlaceType === "kostel" && !!pickerPlace;
  }, [pickerType, pickerPlaceType, pickerPlace, isKremaceBezObraduSimple]);

  const isVehicleRequired = useMemo(() => {
    if (!pickerType) return false;
    if (pickerType === "prevoz") return true;
    if (!pickerPlace || pickerPlace === "__manual__") return false;
    return !isCeskyKrumlovCeremonyHall;
  }, [pickerType, pickerPlace, isCeskyKrumlovCeremonyHall]);

  const needsVehicleForCeremony = useMemo(() => {
    if (!pickerType) return false;
    if (pickerType === "prevoz") return true;
    return true;
  }, [pickerType]);

  useEffect(() => {
    if (!pickerLeader) return;
    const invalid = availableLeaderOptions.find((x) => x.id === pickerLeader && x.disabled);
    if (invalid) setPickerLeader("");
  }, [pickerLeader, availableLeaderOptions]);

  useEffect(() => {
    if (!pickerSupport) return;
    const invalid = availableSupportOptions.find((x) => x.id === pickerSupport && x.disabled);
    if (invalid) setPickerSupport("");
  }, [pickerSupport, availableSupportOptions]);

  useEffect(() => {
    if (!pickerVehicle) return;
    const invalid = availableVehicleOptions.find((x) => x.id === pickerVehicle && x.disabled);
    if (invalid) setPickerVehicle("");
  }, [pickerVehicle, availableVehicleOptions]);

  useEffect(() => {
    setPickerPlace("");
    setPickerCeremonyVariant("");
    setPickerChurchServiceType("");
  }, [pickerPlaceType]);

  useEffect(() => {
    if (pickerType === "prevoz") {
      setPickerCeremonyVariant("");
      setPickerChurchServiceType("");
    }
  }, [pickerType]);

  useEffect(() => {
    if (pickerPlaceType !== "kostel" && pickerChurchServiceType) {
      setPickerChurchServiceType("");
    }
  }, [pickerPlaceType, pickerChurchServiceType]);

  const personConflictCount = useMemo(
    () => new Set(entries.flatMap((x) => (x.conflicts || []).filter((c) => c.type === "person").map(getConflictKey))).size,
    [entries]
  );

  const vehicleConflictCount = useMemo(
    () => new Set(entries.flatMap((x) => (x.conflicts || []).filter((c) => c.type === "vehicle").map(getConflictKey))).size,
    [entries]
  );

  const placeConflictCount = useMemo(
    () => new Set(entries.flatMap((x) => (x.conflicts || []).filter((c) => c.type === "place").map(getConflictKey))).size,
    [entries]
  );

  const missingCount = useMemo(
    () => new Set(entries.flatMap((x) => (x.conflicts || []).filter((c) => c.type === "missing_assignments").map(getConflictKey))).size,
    [entries]
  );

  const totalProblemCount = useMemo(
    () => new Set(entries.flatMap((x) => (x.conflicts || []).map(getConflictKey))).size,
    [entries]
  );

  const needsPlaceForCreate = requiresClassicCeremonyPlace
    ? !!pickerPlace
    : (isKremaceBezObraduVystaveni ? !!pickerPlace : true);

  const isLeaderBlocked = !!(
    pickerLeader &&
    availableLeaderOptions.some((x) => x.id === pickerLeader && x.disabled)
  );

  const isSupportBlocked = !!(
    pickerSupport &&
    availableSupportOptions.some((x) => x.id === pickerSupport && x.disabled)
  );

  const isVehicleBlocked = !!(
    pickerVehicle &&
    availableVehicleOptions.some((x) => x.id === pickerVehicle && x.disabled)
  );

  const isSpeakerBlocked = !!(
    pickerSpeaker &&
    pickerSpeaker !== "dalsi" &&
    pickerSpeaker !== "bez_recnice" &&
    pickerSpeaker !== pickerLeader &&
    availableSpeakerOptions.some((x) => x.id === pickerSpeaker && x.disabled)
  );

  const createBlockReasons = useMemo(() => {
    const reasons = [];
    if (!pickerTime) reasons.push("chybí čas");
    if (!pickerType) reasons.push("chybí typ zakázky");
    if (!needsPlaceForCreate) reasons.push("chybí místo");
    if (isKremaceBezObraduSimple && !pickerPlaceAddressKrematorium) reasons.push("chybí krematorium");
    if (showCeremonyVariantPicker && !pickerCeremonyVariant) reasons.push("chybí varianta rozloučení");
    if (showChurchServiceTypePicker && !pickerChurchServiceType) reasons.push("chybí typ kostelního rozloučení");
    if (isVehicleRequired && !pickerVehicle) reasons.push("chybí auto");
    if (selectedTimeStatus?.disabled) reasons.push("vybraný čas koliduje");
    if (isLeaderBlocked) reasons.push("vedoucí je obsazený");
    if (isSupportBlocked) reasons.push("druhý člověk je obsazený");
    if (isSpeakerBlocked) reasons.push("řečnice je obsazená");
    if (isVehicleBlocked) reasons.push("auto je obsazené");
    return reasons;
  }, [
    pickerTime,
    pickerType,
    needsPlaceForCreate,
    isKremaceBezObraduSimple,
    pickerPlaceAddressKrematorium,
    showCeremonyVariantPicker,
    pickerCeremonyVariant,
    showChurchServiceTypePicker,
    pickerChurchServiceType,
    isVehicleRequired,
    pickerVehicle,
    selectedTimeStatus,
    isLeaderBlocked,
    isSupportBlocked,
    isSpeakerBlocked,
    isVehicleBlocked,
  ]);

  const canCreateFromCalendar = createBlockReasons.length === 0;

  const resetPicker = () => {
    setPickerTime("");
    setPickerType("");
    setPickerPlaceType("");
    setPickerPlace("");
    setPickerCeremonyVariant("");
    setPickerChurchServiceType("");
    setPickerClient("");
    setPickerDeceased("");
    setPickerNote("");
    setPickerLeader("");
    setPickerSupport("");
    setPickerSpeaker("");
    setPickerSpeakerCustom("");
    setPickerMusicType("");
    setPickerMusicPerson("");
    setPickerMusicPersonCustom("");
    setPickerVehicle("");
    setPickerFarar("");
    setPickerTemplate("");
    setPickerDeathScenario("");
    setPickerPrubeh("");
    setPickerPlaceAddressKrematorium("");
    setPickerDay("");
  };

  return (
    <div style={panelStyle}>
      <div style={topRowStyle}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#111827" }}>Týdenní přehled Pohřebka 2</div>
          <div style={{ marginTop: 4, color: "#6b7280", fontSize: 14 }}>
            Přehled kdo co dělá, kdy, kde a čím. Tvrdé kolize řeší lidi a auta, měkké kolize řeší místo a neobsazené role.
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" onClick={goToPreviousWeek} style={actionBtnStyle}>
              ← Předchozí týden
            </button>
            <button type="button" onClick={goToCurrentWeek} style={actionBtnStyle}>
              Tento týden
            </button>
            <button type="button" onClick={goToNextWeek} style={actionBtnStyle}>
              Další týden →
            </button>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 42,
                padding: "0 12px",
                borderRadius: 12,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                color: "#0f172a",
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              {weekRangeLabel}
            </span>

              <select
                value={familyFilter}
                onChange={(e) => setFamilyFilter(String(e.target.value || ""))}
                style={{ ...pickerInputStyle, width: 220 }}
              >
                <option value="">Rodinná část: všechny</option>
                <option value="navazuje_rodina">Navazuje rodinná část</option>
                <option value="jen_vyjezd">Jen výjezd</option>
              </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={badgeStyle(personConflictCount ? "kolize" : "ok")}>Lidé: {personConflictCount}</span>
          <span style={badgeStyle(vehicleConflictCount ? "kolize" : "ok")}>Auta: {vehicleConflictCount}</span>
          <span style={badgeStyle(placeConflictCount ? "soft" : "ok")}>Místa: {placeConflictCount}</span>
          <span style={badgeStyle(missingCount ? "soft" : "ok")}>Neobsazeno: {missingCount}</span>
          <span style={badgeStyle(totalProblemCount ? "kolize" : "ok")}>Celkem problémů: {totalProblemCount}</span>
        </div>
      </div>

      <div style={weekGridStyle}>
        {weekDays.map((day) => {
          const rows = byDay.get(day) || [];
          const isActive = selectedDate === day;

          return (
            <div
              key={day}
              style={dayCardStyle(isActive)}
              onClick={() => {
                if (typeof onSelectDate === "function") onSelectDate(day);
                setPickerDay(day);
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <div style={{ fontWeight: 900, color: "#111827" }}>{formatDateLabel(day)}</div>
                <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>{rows.length}×</div>
              </div>

              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={badgeStyle("ok")}>{day}</span>
                {rows.some((x) => x.hardConflicts?.length) ? <span style={badgeStyle("kolize")}>Tvrdá kolize</span> : null}
                {!rows.some((x) => x.hardConflicts?.length) && rows.some((x) => x.softConflicts?.length) ? (
                  <span style={badgeStyle("soft")}>Upozornění</span>
                ) : null}
              </div>

              <div style={{ marginTop: 12 }}>
                {rows.length ? rows.map((row) => (
                  <div key={row.id} style={rowCardStyle(row)}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={badgeStyle(row.kind)}>{row.title}</span>
                        <span style={calendarStatusBadgeStyle(row.orderStatus)}>
                          {row.orderStatus?.label || "Rozpracováno"}
                        </span>
                          <span style={calendarFamilyBadgeStyle(row.familyStatus)}>
                            {row.familyStatus?.label || "Jen výjezd"}
                          </span>
                            <span style={calendarVyjezdInvoiceBadgeStyle(row.vyjezdInvoiceStatus)}>
                              {row.vyjezdInvoiceStatus?.label || "Neodesláno"}
                            </span>
                        {row.hardConflicts?.length ? <span style={badgeStyle("kolize")}>Kolize</span> : null}
                        {!row.hardConflicts?.length && row.softConflicts?.length ? <span style={badgeStyle("soft")}>Pozor</span> : null}
                      </div>

                        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          {row.orderId ? <span style={badgeStyle("ok")}>ID {row.orderId}</span> : null}
                          <span style={calendarFinanceBadgeStyle(row.financeStatus)}>
                            {row.financeStatus?.label || "Bez částek"}
                          </span>
                          {Number(row.financeStatus?.total || 0) > 0 ? (
                            <span style={badgeStyle("ok")}>
                              {Math.round(Number(row.financeStatus?.total || 0))} Kč
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {row.place ? (
                        <div style={{ fontSize: 13, color: "#374151", fontWeight: 800 }}>
                          {row.place}
                        </div>
                      ) : null}

                      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                        {row.zesnuly}
                      </div>

                      {row.objednavatel ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          Objednavatel: {row.objednavatel}
                        </div>
                      ) : null}

                      {row.farar ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          Farář: {row.farar}
                        </div>
                      ) : null}

                      {row.roleAssignments?.length ? (
                        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.4 }}>
                          <strong>Lidé:</strong>{" "}
                          {row.roleAssignments
                            .map((x) => `${getPersonRoleLabel(x.role)}: ${x.personLabel || x.personId || "-"}`)
                            .join(" • ")}
                        </div>
                      ) : null}

                      {row.vehicleAssignments?.length ? (
                        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.4 }}>
                          <strong>Auta:</strong>{" "}
                          {row.vehicleAssignments
                            .map((x) => `${getVehicleRoleLabel(x.role)}: ${x.vehicleLabel || x.vehicleId || "-"}`)
                            .join(" • ")}
                        </div>
                      ) : null}

                      {(row.conflicts || []).length ? (
                        <div style={{ marginTop: 4, display: "grid", gap: 4 }}>
                          {row.conflicts.map((c) => (
                            <div
                              key={getConflictKey(c)}
                              style={{
                                fontSize: 12,
                                lineHeight: 1.35,
                                color: c.severity === "hard" ? "#991b1b" : "#9a3412",
                                background: c.severity === "hard" ? "#fff1f2" : "#fff7ed",
                                border: `1px solid ${c.severity === "hard" ? "#fecdd3" : "#fed7aa"}`,
                                borderRadius: 10,
                                padding: "6px 8px",
                              }}
                            >
                              {summarizeConflict(c)}
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {row.note ? (
                        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
                          {row.note}
                        </div>
                      ) : null}


                    <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={actionBtnStyle}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (typeof onOpenOrder === "function" && row.orderId) {
                            const preferredTab =
                              row.kind === "prevoz" || row.typUdalosti === "prevoz"
                                ? "prevoz"
                                : row.kind === "sjednani" || row.typUdalosti === "sjednani"
                                  ? "zaklad"
                                  : "obrad";
                            onOpenOrder(row.orderId, preferredTab, row);
                          }
                        }}
                      >
                        Otevřít
                      </button>
                    </div>
                  </div>
                )) : (
                  <div style={{ color: "#9ca3af", fontSize: 14 }}>Bez události</div>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                {pickerDay === day ? (
                  <div style={timePickerWrapStyle} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
                      1. Vyber čas pro {day}
                    </div>

                    <div style={timeGridStyle}>
                      {TIME_OPTIONS.map((time) => {
                        const item = timeOptionStatuses.find((x) => x.time === time) || { time, disabled: false, reason: "" };
                        return (
                          <button
                            key={item.time}
                            type="button"
                            disabled={item.disabled}
                            style={{
                              ...timeBtnStyle,
                              background: pickerTime === item.time ? "#eff6ff" : item.disabled ? "#f3f4f6" : "#fff",
                              borderColor: pickerTime === item.time ? "#2563eb" : item.disabled ? "#e5e7eb" : "#cbd5e1",
                              color: pickerTime === item.time ? "#1d4ed8" : item.disabled ? "#9ca3af" : "#111827",
                              cursor: item.disabled ? "not-allowed" : "pointer",
                              opacity: item.disabled ? 0.7 : 1,
                            }}
                            title={item.reason || ""}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.disabled) return;
                              setPickerTime(item.time);
                            }}
                          >
                            {item.time}{item.disabled ? " • obsazeno" : ""}
                          </button>
                        );
                      })}
                    </div>

                    <div style={infoBoxStyle}>
                      Program hlídá celé okno obsazení: od odjezdu z firmy, přes obřad, až po návrat.
                    </div>

                    {selectedTimeStatus?.disabled ? (
                      <div style={errorBoxStyle}>
                        Vybraný čas koliduje: {selectedTimeStatus.reason || "obsazeno"}.
                      </div>
                    ) : null}

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      2. Vyber typ zakázky
                    </div>

                    <div style={typeGridStyle}>
                      {TYPE_OPTIONS.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          style={{
                            ...typeBtnStyle,
                            background: pickerType === item.value ? "#eff6ff" : "#fff",
                            borderColor: pickerType === item.value ? "#2563eb" : "#cbd5e1",
                            color: pickerType === item.value ? "#1d4ed8" : "#111827",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!pickerTime) return;
                            setPickerType(item.value);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    {!isKremaceBezObraduSimple || isKremaceBezObraduVystaveni ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          3. Typ místa rozloučení
                        </div>

                        <div style={placeGridStyle}>
                          {PLACE_TYPE_OPTIONS.map((item) => {
                            const selected = pickerPlaceType === item.value;
                            return (
                              <button
                                key={item.value}
                                type="button"
                                style={{
                                  ...placeBtnStyle,
                                  background: selected ? "#eff6ff" : "#fff",
                                  borderColor: selected ? "#2563eb" : "#cbd5e1",
                                  color: selected ? "#1d4ed8" : "#111827",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!pickerTime || !pickerType) return;
                                  setPickerPlaceType(item.value);
                                  setPickerPlace("");
                                  setPickerCeremonyVariant("");
                                  setPickerChurchServiceType("");
                                }}
                              >
                                {item.label}
                              </button>
                            );
                          })}
                        </div>

                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          4. Vyber místo
                        </div>

                        <div style={placeGridStyle}>
                          {availablePlacesForPicker.map((item) => {
                            const disabled = !!item.disabled;
                            const selected = pickerPlace === item.value;

                            return (
                              <button
                                key={item.value}
                                type="button"
                                disabled={disabled}
                                style={{
                                  ...placeBtnStyle,
                                  background: selected ? "#eff6ff" : disabled ? "#f3f4f6" : "#fff",
                                  borderColor: selected ? "#2563eb" : disabled ? "#e5e7eb" : "#cbd5e1",
                                  color: selected ? "#1d4ed8" : disabled ? "#9ca3af" : "#111827",
                                  cursor: disabled ? "not-allowed" : "pointer",
                                  opacity: disabled ? 0.7 : 1,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!pickerTime || !pickerType || !pickerPlaceType || disabled) return;
                                  setPickerPlace(item.value);
                                }}
                                title={disabled ? (item.reason || "Místo je obsazené") : ""}
                              >
                                {item.label}{disabled ? ` • ${item.reason || "obsazeno"}` : ""}
                              </button>
                            );
                          })}
                        </div>

                        {showCeremonyVariantPicker ? (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                              5. Varianta rozloučení
                            </div>

                            <div style={placeGridStyle}>
                              {CEREMONY_VARIANTS.map((item) => {
                                const selected = pickerCeremonyVariant === item.id;
                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    style={{
                                      ...placeBtnStyle,
                                      background: selected ? "#eff6ff" : "#fff",
                                      borderColor: selected ? "#2563eb" : "#cbd5e1",
                                      color: selected ? "#1d4ed8" : "#111827",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!pickerPlace) return;
                                      setPickerCeremonyVariant(item.id);
                                    }}
                                  >
                                    {item.label}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        ) : null}

                        {showChurchServiceTypePicker ? (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                              6. Typ kostelního rozloučení
                            </div>

                            <div style={placeGridStyle}>
                              {CHURCH_SERVICE_TYPES.map((item) => {
                                const selected = pickerChurchServiceType === item.id;
                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    style={{
                                      ...placeBtnStyle,
                                      background: selected ? "#eff6ff" : "#fff",
                                      borderColor: selected ? "#2563eb" : "#cbd5e1",
                                      color: selected ? "#1d4ed8" : "#111827",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!pickerPlace) return;
                                      setPickerChurchServiceType(item.id);
                                    }}
                                  >
                                    {item.label}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        ) : null}

                        {selectedTiming ? (
                          <div style={infoBoxStyle}>
                            Jízda tam: {selectedTiming.driveMinutes} min • příprava před obřadem: {selectedTiming.bufferBeforeMin} min •
                            rozloučení: {selectedTiming.durationMin} min • návrat: {selectedTiming.bufferAfterMin} min •
                            celkové obsazení: {selectedTiming.totalBlockedMin} min
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      7. Vzor zakázky
                    </div>

                    <select
                      value={pickerTemplate}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPickerTemplate(String(e.target.value || ""))}
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    >
                      <option value="">
                        {availableTemplateOptions.length ? "Bez vzoru / ručně" : "Žádný uložený vzor pro tento typ"}
                      </option>
                      {availableTemplateOptions.map((item) => (
                        <option key={item.id} value={item.id}>{item.label}</option>
                      ))}
                    </select>

                    <div style={infoBoxStyle}>
                      Zde se načítají tvoje skutečně uložené šablony pro vybraný typ zakázky.
                    </div>

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      8. Místo / scénář úmrtí
                    </div>

                    <select
                      value={pickerDeathScenario}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        setPickerDeathScenario(String(e.target.value || ""));
                        setPickerTemplate("");
                      }}
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    >
                      <option value="">Neurčeno / všechny vzory</option>
                      <option value="nemocnice_ck">Nemocnice ČK</option>
                      <option value="doma">Úmrtí doma</option>
                      <option value="wagnerka">Wagnerka</option>
                      <option value="mimo_ck">Mimo ČK</option>
                    </select>

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      9. Další průběh
                    </div>

                    <select
                      value={pickerPrubeh}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        setPickerPrubeh(String(e.target.value || ""));
                        setPickerTemplate("");
                      }}
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    >
                      <option value="">Vyber průběh</option>
                      {getPrubehOptionsForTyp(pickerType)
                        .filter((item) =>
                          getAllowedPrubehKeysForScenario(pickerType, pickerDeathScenario).includes(String(item?.value || "").trim())
                        )
                        .map((item) => (
                          <option key={item.value || "empty"} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                    </select>

                    {isKremaceBezObraduSimple ? (
                      <>
                        {isKremaceBezObraduVystaveni ? (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                              9A. Místo vystavení
                            </div>

                            <div style={placeGridStyle}>
                              <button
                                type="button"
                                style={{
                                  ...placeBtnStyle,
                                  background: pickerPlaceType === "obradni_sin" ? "#eff6ff" : "#fff",
                                  borderColor: pickerPlaceType === "obradni_sin" ? "#2563eb" : "#cbd5e1",
                                  color: pickerPlaceType === "obradni_sin" ? "#1d4ed8" : "#111827",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPickerPlaceType("obradni_sin");
                                  setPickerPlace("obradni_sin_ck");
                                }}
                              >
                                Obřadní síň Český Krumlov
                              </button>

                              <button
                                type="button"
                                style={{
                                  ...placeBtnStyle,
                                  background: pickerPlaceType === "jine" ? "#eff6ff" : "#fff",
                                  borderColor: pickerPlaceType === "jine" ? "#2563eb" : "#cbd5e1",
                                  color: pickerPlaceType === "jine" ? "#1d4ed8" : "#111827",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPickerPlaceType("jine");
                                  setPickerPlace("__manual__");
                                }}
                              >
                                Jiné místo ručně
                              </button>
                            </div>

                            {pickerPlaceType === "jine" ? (
                              <input
                                type="text"
                                value={pickerPlace}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setPickerPlace(String(e.target.value || ""))}
                                placeholder="Doplň místo vystavení ručně"
                                style={{ ...pickerInputStyle, marginTop: 8 }}
                              />
                            ) : null}
                          </>
                        ) : null}

                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          10A. Krematorium
                        </div>

                        <select
                          value={pickerPlaceAddressKrematorium || ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setPickerPlaceAddressKrematorium(String(e.target.value || ""))}
                          style={{ ...pickerInputStyle, marginTop: 8 }}
                        >
                          <option value="">Vyber krematorium</option>
                          <option value="Pražská tř. 2275/108, 370 04 České Budějovice">Krematorium České Budějovice</option>
                          <option value="Soběslavská 2502, 390 02 Tábor">Krematorium Tábor</option>
                          <option value="Komenského 84, 397 01 Písek">Krematorium Písek</option>
                          <option value="Máchova 249, 386 01 Strakonice">Krematorium Strakonice</option>
                        </select>
                      </>
                    ) : null}

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      10. Objednavatel
                    </div>

                    <input
                      type="text"
                      value={pickerClient}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPickerClient(String(e.target.value || ""))}
                      placeholder="Např. Jan Novák"
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    />

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      11. Zesnulý
                    </div>

                    <input
                      type="text"
                      value={pickerDeceased}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPickerDeceased(String(e.target.value || ""))}
                      placeholder="Např. Karel Novák"
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    />

                    {showFararField ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          12. Farář (volitelné)
                        </div>

                        <input
                          type="text"
                          value={pickerFarar}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setPickerFarar(String(e.target.value || ""))}
                          placeholder="Např. P. Novák"
                          style={{ ...pickerInputStyle, marginTop: 8 }}
                        />
                      </>
                    ) : null}

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      13. Poznámka
                    </div>

                    <input
                      type="text"
                      value={pickerNote}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPickerNote(String(e.target.value || ""))}
                      placeholder="Např. řečník, hudba, zvláštní požadavek"
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    />

                    <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                      14. Vedoucí obřadu / hlavní člověk
                    </div>

                    <select
                      value={pickerLeader}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setPickerLeader(String(e.target.value || ""))}
                      style={{ ...pickerInputStyle, marginTop: 8 }}
                    >
                      <option value="">Vyber člověka</option>
                      {availableLeaderOptions.map((item) => (
                        <option key={item.id} value={item.id} disabled={!!item.disabled}>
                          {item.displayLabel || buildDisabledOptionLabel(item.label, !!item.disabled)}
                        </option>
                      ))}
                    </select>

                    {!pickerLeader ? (
                      <div style={infoBoxStyle}>
                        Ve výběru se blokují lidé, kteří už mají v tomhle čase nebo překryvu jiný obřad.
                      </div>
                    ) : null}

                    {showSupportField && (!needsCeremonyPlace || !isCeskyKrumlovCeremonyHall) ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          15. Druhý člověk / doprovod
                        </div>

                        <select
                          value={pickerSupport}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setPickerSupport(String(e.target.value || ""))}
                          style={{ ...pickerInputStyle, marginTop: 8 }}
                        >
                          <option value="">Vyber druhého člověka</option>
                          {availableSupportOptions.map((item) => (
                            <option key={item.id} value={item.id} disabled={!!item.disabled}>
                              {item.displayLabel || buildDisabledOptionLabel(item.label, !!item.disabled)}
                            </option>
                          ))}
                        </select>

                        {!pickerSupport ? (
                          <div style={infoBoxStyle}>
                            Druhý člověk se blokuje podle stejné časové logiky jako vedoucí obřadu.
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {showSpeakerField ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                          16. Řečnice / mluvené slovo
                        </div>

                        <select
                            value={pickerSpeaker}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setPickerSpeaker(String(e.target.value || ""))}
                            style={{ ...pickerInputStyle, marginTop: 8 }}
                          >
                            <option value="">Vyber řečnici / variantu</option>
                            {availableSpeakerOptions.map((item) => (
                              <option key={item.id} value={item.id} disabled={!!item.disabled}>
                                {item.displayLabel || item.label}
                              </option>
                            ))}
                          </select>

                        {pickerSpeaker === "dalsi" ? (
                          <input
                            type="text"
                            value={pickerSpeakerCustom}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setPickerSpeakerCustom(String(e.target.value || ""))}
                            placeholder="Doplň řečnici ručně"
                            style={{ ...pickerInputStyle, marginTop: 8 }}
                          />
                        ) : null}

                        {showMusicField ? (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                              17. Hudba
                            </div>

                            <select
                              value={pickerMusicType}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setPickerMusicType(String(e.target.value || ""))}
                              style={{ ...pickerInputStyle, marginTop: 8 }}
                            >
                              <option value="">Vyber typ hudby</option>
                              {MUSIC_TYPE_OPTIONS.map((item) => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                              ))}
                            </select>

                            {pickerMusicType === "reprodukovana" ? (
                              <>
                                <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                                  18. Kdo pouští hudbu
                                </div>

                                <select
                                  value={pickerMusicPerson}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setPickerMusicPerson(String(e.target.value || ""))}
                                  style={{ ...pickerInputStyle, marginTop: 8 }}
                                >
                                  <option value="">Vyber člověka</option>
                                  {MUSIC_PERSON_OPTIONS.map((item) => (
                                    <option key={item.id} value={item.id}>{item.label}</option>
                                  ))}
                                </select>

                                {pickerMusicPerson === "dalsi" ? (
                                  <input
                                    type="text"
                                    value={pickerMusicPersonCustom}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setPickerMusicPersonCustom(String(e.target.value || ""))}
                                    placeholder="Doplň hudbu ručně"
                                    style={{ ...pickerInputStyle, marginTop: 8 }}
                                  />
                                ) : null}
                              </>
                            ) : null}
                          </>
                        ) : null}
                      </>
                    ) : null}

                      {needsVehicleForCeremony ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 900, color: "#111827", marginTop: 12 }}>
                            18. Auto {isVehicleRequired ? "(povinné)" : "(volitelné)"}
                          </div>

                          <select
                            value={pickerVehicle}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setPickerVehicle(String(e.target.value || ""))}
                            style={{ ...pickerInputStyle, marginTop: 8 }}
                          >
                            <option value="">
                              {isVehicleRequired ? "Vyber auto" : "Bez auta / není potřeba"}
                            </option>
                            {availableVehicleOptions.map((item) => (
                              <option key={item.id} value={item.id} disabled={!!item.disabled}>
                                {item.displayLabel || buildDisabledOptionLabel(item.label, !!item.disabled)}
                              </option>
                            ))}
                          </select>

                          {pickerVehicle && availableVehicleOptions.find((x) => x.id === pickerVehicle && x.disabled) ? (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: "#991b1b",
                                background: "#fff1f2",
                                border: "1px solid #fecdd3",
                                borderRadius: 10,
                                padding: "6px 8px",
                                lineHeight: 1.4,
                              }}
                            >
                              Toto auto je už obsazené:{" "}
                              {availableVehicleOptions.find((x) => x.id === pickerVehicle)?.displayLabel || "obsazeno"}
                            </div>
                          ) : null}

                          {!pickerVehicle ? (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                color: "#6b7280",
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderRadius: 10,
                                padding: "6px 8px",
                                lineHeight: 1.4,
                              }}
                            >
                              {isVehicleRequired
                                ? "Pro tento typ zakázky je potřeba vybrat auto."
                                : "Auto můžeš vybrat jen pokud je potřeba."}
                            </div>
                          ) : null}
                        </>
                      ) : null}

                      <div
                        style={canCreateFromCalendar ? successBoxStyle : warnBoxStyle}
                    >
                      {canCreateFromCalendar ? (
                        <strong>Vše je připravené. Zakázku můžeš bezpečně otevřít.</strong>
                      ) : (
                        <>
                          <strong>Ještě nelze otevřít zakázku.</strong>{" "}
                          Chybí nebo koliduje: {createBlockReasons.join(" • ")}.
                        </>
                      )}
                    </div>

                    <button
                      type="button"
                      disabled={!canCreateFromCalendar}
                      style={{
                        ...actionBtnStyle,
                        width: "100%",
                        marginTop: 10,
                        background: canCreateFromCalendar ? "#eff6ff" : "#f8fafc",
                        borderColor: canCreateFromCalendar ? "#2563eb" : "#d1d5db",
                        color: canCreateFromCalendar ? "#1d4ed8" : "#6b7280",
                        cursor: canCreateFromCalendar ? "pointer" : "not-allowed",
                        opacity: canCreateFromCalendar ? 1 : 0.75,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canCreateFromCalendar) return;

                        const mistoLabel = needsCeremonyPlace ? getCeremonyPlaceLabel(pickerPlace) : "";
                        const selectedTemplateMeta =
                          availableTemplateOptions.find((item) => String(item?.id || "") === String(pickerTemplate || "")) || null;
                        const resolvedCoolingScenario =
                          String(selectedTemplateMeta?.coolingScenario || "").trim();

                        if (typeof onCreateFromDate === "function") {
                          onCreateFromDate(day, pickerTime, pickerType, pickerPlace, pickerClient, pickerDeceased, pickerNote, {
                            leader: pickerLeader,
                            support: pickerSupport,
                            speaker: pickerSpeaker,
                            speakerCustom: pickerSpeakerCustom,
                            musicType: pickerMusicType,
                            musicPerson: pickerMusicPerson,
                            musicPersonCustom: pickerMusicPersonCustom,
                            vehicle: pickerVehicle,
                            farar: pickerFarar,
                            templateId: pickerTemplate,
                            coolingScenario: resolvedCoolingScenario,
                            deathScenario: pickerDeathScenario,
                            prubehKey: pickerPrubeh,
                            placeType: pickerPlaceType,
                            placeLabel: mistoLabel,
                            misto: mistoLabel,
                            mistoId: pickerPlace,
                            mistoSkupina: pickerPlaceType,
                            ceremonyVariant: isKremaceBezObraduVystaveni ? "vystaveni" : pickerCeremonyVariant,
                            churchServiceType: pickerChurchServiceType,
                            krematoriumAdresa: pickerPlaceAddressKrematorium,
                          });
                        }

                        resetPicker();
                      }}
                    >
                      Otevřít novou zakázku
                    </button>

                    <button
                      type="button"
                      style={{ ...actionBtnStyle, width: "100%", marginTop: 10 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        resetPicker();
                      }}
                    >
                      Vyčistit výběr
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    style={{ ...actionBtnStyle, width: "100%" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPickerDay(day);
                      if (typeof onSelectDate === "function") onSelectDate(day);
                    }}
                  >
                    Přidat novou zakázku
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
